import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import OptimizedImage from "@/components/OptimizedImage";
import { useAllServiceBoxSettings, getImageUrl, ServiceBoxSettings } from "@/hooks/useAllServiceBoxSettings";
import { Skeleton } from "@/components/ui/skeleton";

interface Service {
  title: string;
  subtitle: string;
  cta: string;
  link: string;
  serviceKey: string;
}

const services: Service[] = [
  {
    title: "Föreställningar",
    subtitle: "Se improviserade sketcher",
    cta: "Kommande",
    link: "/forestallningar",
    serviceKey: "forestallningar",
  },
  {
    title: "Kurser",
    subtitle: "För dig som vill utvecklas på scen",
    cta: "Utforska",
    link: "/kurser",
    serviceKey: "kurser",
  },
  {
    title: "Underhållning",
    subtitle: "För företag och privata tillställningar",
    cta: "Läs mer",
    link: "/boka-oss",
    serviceKey: "underhallning",
  },
];

const ServiceBox = ({ service, settings, hasFreeShow }: { service: Service; settings: ServiceBoxSettings; hasFreeShow?: boolean }) => {
  const imageUrl = getImageUrl(settings.image_file);
  const showFreeBadge = service.serviceKey === 'forestallningar' && hasFreeShow;

  return (
    <Link
      to={service.link}
      className="group flex flex-col bg-[rgb(var(--card-background))] overflow-hidden transition-transform duration-300 w-full cursor-pointer"
    >
      {/* Bild‐sektion = halva höjden */}
      <div className="relative h-[200px] lg:h-[250px] overflow-hidden">
        {showFreeBadge && (
          <div
            className="absolute top-3 right-3 z-10 px-3 py-1 text-sm font-bold rounded-full font-rajdhani tracking-wide"
            style={{ backgroundColor: 'rgb(var(--primary-red))', color: 'rgb(var(--white))' }}
          >
            Gratis
          </div>
        )}
        {imageUrl ? (
          <OptimizedImage
            src={imageUrl}
            alt={service.title}
            className="w-full h-full group-hover:scale-105 transition-transform duration-300"
            layout="full-width"
            loading="eager"
            style={{
              objectFit: (settings?.object_fit || "cover") as React.CSSProperties['objectFit'],
              objectPosition: `${settings?.position_x || "50"}% ${settings?.position_y || "50"}%`,
            }}
          />
        ) : (
          <div className="relative w-full h-full bg-muted" />
        )}
      </div>

      {/* Innehåll */}
      <div className="flex-1 px-4 py-6 flex flex-col justify-between text-center bg-[rgb(var(--card-background))]">
        <div className="space-y-2">
          <h1 className="text-center whitespace-nowrap">{service.title}</h1>
          <p className="font-satoshi text-[16px] text-text-black leading-relaxed">{service.subtitle}</p>
        </div>
        <div className="mt-4 mx-[10px]">
          <Button
            variant="homepage"
            size="homepage"
            className="flex justify-between items-center w-full pointer-events-none"
          >
            <span className="leading-[1.0]">{service.cta}</span>
            <span className="text-2xl font-bold">→</span>
          </Button>
        </div>
      </div>
    </Link>
  );
};

export default function ServiceBoxes({ hasFreeShow }: { hasFreeShow?: boolean }) {
  const { data: allSettings, isLoading } = useAllServiceBoxSettings();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 min-[690px]:grid-cols-3 gap-[15px] justify-items-center max-w-[930px] mx-auto">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col bg-[rgb(var(--card-background))] overflow-hidden w-full">
            <Skeleton className="h-[200px] lg:h-[250px] w-full rounded-none" />
            <div className="p-6">
              <Skeleton className="h-32 w-full rounded-none" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!allSettings) return null;

  return (
    <div className="grid grid-cols-1 min-[690px]:grid-cols-3 gap-[15px] justify-items-center max-w-[930px] mx-auto">
      {services.map((service, idx) => (
        <ServiceBox 
          key={idx} 
          service={service} 
          settings={allSettings[service.serviceKey as keyof typeof allSettings]}
          hasFreeShow={hasFreeShow}
        />
      ))}
    </div>
  );
}
